import './WebTrafficOptimization.scss';
import { JSX, useCallback, useEffect, useState } from 'react';
import { Plover, WtoDomain } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import { emailSelector } from '../../redux/user';
import { awaitJSON, WtoLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { Menubar } from './Menubar/Menubar';
import { ActivateModal } from './Modals/ActivateModal';
import { ArchiveModal } from './Modals/ArchiveModal';
import { DuplicateModal } from './Modals/DuplicateModal';
import { RouteDetailsForm } from './RouteDetailsForm';
import { RouteRules } from './RouteRules/RouteRules';
import { RouteTable } from './RouteTable/RouteTable';
import { WtoTab, WtoTabType } from './web-traffic-optimization-constants';
import { formObjDefault, WtoFormProvider } from './wto-form-context';

const BASE_URL = 'web-traffic-optimization';

const NAVS = [
  { label: 'Routes', key: WtoTab.ROUTE_TABLE },
  { label: 'Route Details', key: WtoTab.ROUTE_DETAILS },
  { label: 'Rules', key: WtoTab.ROUTE_RULES }
];

export const WebTrafficOptimization = (): JSX.Element => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const userEmail = useAppSelector(emailSelector);

  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [cancelConfirmation, setCancelConfirmation] = useState<WtoTabType>();
  const [modal, setModal] = useState<string>('');
  const [modalRowData, setModalRowData] = useState<Plover>(formObjDefault);
  const [formEdited, setFormEdited] = useState(false);
  const [splitsEdited, setSplitsEdited] = useState(false);
  const [domains, setDomains] = useState<WtoDomain[]>([]);
  const [plovers, setPlovers] = useState<Plover[]>([]);

  const { tab, goToTab } = useTabbedNav<WtoTabType>({
    baseURL: BASE_URL,
    defaultKey: WtoTab.ROUTE_TABLE,
    navs: Object.values(WtoTab)
  });

  // Route Details and Rules aren't reachable from the nav bar directly — only via the flow's own
  // buttons (a table row, "Continue to Rules", etc.), same as the old breadcrumb trail only ever
  // showed crumbs for how deep into the flow the user already was.
  const visibleNavs = NAVS.slice(0, NAVS.findIndex(nav => nav.key === tab) + 1);

  const fetchPlovers = useCallback(async () => {
    setLoading(true);
    try {
      const ploverRes = await WtoLambdaFetch('/plovers', {
        params: { company: cid }
      });
      const ploverData = await awaitJSON<{ data: Plover[] }>(ploverRes);
      const formattedPlovers = ploverData.data.map(plover => ({
        ...plover,
        rules: plover.rules.map(rule => {
          const formattedConditions: {
            trigger: string;
            rule: string;
            value: string;
            operator: string;
          }[] = [];
          rule.conditions.forEach(condition => {
            if (
              Array.isArray(condition) &&
              condition.length === 4 &&
              condition[0] != null &&
              condition[1] != null &&
              condition[2] != null &&
              condition[3] != null
            ) {
              formattedConditions.push({
                trigger: condition[0],
                rule: condition[1],
                value: condition[2],
                operator: formattedConditions.length === 0 ? 'WHEN' : 'AND'
              });
            } else if (
              typeof condition === 'object' &&
              condition !== null &&
              !Array.isArray(condition) &&
              condition.trigger &&
              condition.rule &&
              condition.value &&
              condition.operator
            ) {
              formattedConditions.push({
                ...condition,
                operator: formattedConditions.length === 0 ? 'WHEN' : 'AND'
              });
            }
          });
          return { ...rule, conditions: formattedConditions };
        })
      }));
      setPlovers(formattedPlovers);
    } catch (e) {
      setError({
        message: `Failed to fetch routes. Error: ${e.message}`,
        reportError: e
      });
      setPlovers([]);
    }
    setLoading(false);
  }, [cid, setError]);

  useEffect(() => {
    (async () => {
      try {
        const domainRes = await WtoLambdaFetch('/domains', {
          params: { company: cid }
        });
        const domainData = await awaitJSON<{ data: WtoDomain[] }>(domainRes);
        setDomains(domainData.data);
        fetchPlovers();
      } catch (e) {
        setError({
          message: `Failed to fetch domains. Error: ${e.message}`,
          reportError: e
        });
        setLoading(false);
      }
    })();
  }, [cid, fetchPlovers, setError]);

  const setModalData = (modalType: string, data: Plover) => {
    setModal(modalType);
    setModalRowData(data);
  };

  // Route Details and Rules are two steps of the same flow, so switching directly between them
  // via the nav bar should preserve form data with no prompt — only leaving the flow for Routes
  // (the table) should warn about and discard unsaved changes, same as the Cancel button.
  const onNav = (newTab: string) => {
    if (tab === newTab) return;
    const leavingUnsavedChanges = (formEdited && !readOnly) || (readOnly && splitsEdited);
    if (newTab === WtoTab.ROUTE_TABLE && leavingUnsavedChanges) {
      setCancelConfirmation(WtoTab.ROUTE_TABLE);
    } else {
      goToTab(newTab as WtoTabType);
    }
  };

  return (
    <WtoFormProvider>
      <Page
        title='Web Traffic Optimization'
        pageType='Web Traffic Optimization'
        navs={visibleNavs}
        selectedNav={tab}
        onNav={onNav}
        actions={domains.length > 0 && <Menubar tab={tab} goToTab={goToTab} loading={loading} />}
      >
        <div className='web-traffic-optimization'>
          {loading ? (
            <FullPageSpinner />
          ) : (
            <>
              {tab === WtoTab.ROUTE_DETAILS && (
                <RouteDetailsForm
                  domains={domains}
                  goToTab={goToTab}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  formEdited={formEdited}
                  setFormEdited={setFormEdited}
                  cancelConfirmation={cancelConfirmation}
                  setCancelConfirmation={setCancelConfirmation}
                  userEmail={userEmail}
                  fetchPlovers={fetchPlovers}
                  companyId={cid}
                />
              )}
              {tab === WtoTab.ROUTE_RULES && (
                <RouteRules
                  goToTab={goToTab}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  formEdited={formEdited}
                  setFormEdited={setFormEdited}
                  setSplitsEdited={setSplitsEdited}
                  cancelConfirmation={cancelConfirmation}
                  setCancelConfirmation={setCancelConfirmation}
                  userEmail={userEmail}
                  fetchPlovers={fetchPlovers}
                  companyId={cid}
                  setModalData={setModalData}
                  plovers={plovers}
                />
              )}
              {tab === WtoTab.ROUTE_TABLE && (
                <RouteTable
                  data={plovers}
                  setModalData={setModalData}
                  goToTab={goToTab}
                  loading={loading}
                  domains={domains}
                />
              )}
            </>
          )}
          <DuplicateModal
            show={modal === 'Duplicate Route'}
            closeModal={() => setModal('')}
            data={modalRowData}
            userEmail={userEmail}
            companyId={cid}
            fetchPlovers={fetchPlovers}
          />
          <ArchiveModal
            show={modal === 'Archive Route'}
            closeModal={() => setModal('')}
            data={modalRowData}
            userEmail={userEmail}
            fetchPlovers={fetchPlovers}
          />
          <ActivateModal
            show={
              modal === 'Activate Route' || modal === 'Create Route' || modal === 'Update Route'
            }
            closeModal={() => setModal('')}
            data={modalRowData}
            userEmail={userEmail}
            fetchPlovers={fetchPlovers}
            action={modal}
            goToTab={goToTab}
          />
        </div>
      </Page>
    </WtoFormProvider>
  );
};
